import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, FileUpload, Input, Skeleton, useToast } from '@bbpos/ui';
import {
  MAX_PRODUCT_IMAGES,
  productImageService,
  type ProductImage,
} from '@/services/product-image.service';
import { uploadProductImage } from '@/pages/product-image-upload';
import { MAX_RAW_UPLOAD_BYTES } from '@/lib/upload-file';

/**
 * The product page's extra photos.
 *
 * One photo per product was the single biggest thing standing between a
 * shopper and a purchase on the public storefront — there was no table to put a
 * second one in, and no way to add one.
 *
 * **The cover stays where it is.** `products.image_url` is read by the POS,
 * receipts, the QR menu and every order line's snapshot; this panel manages
 * only what comes after it. The storefront concatenates the two, cover first.
 *
 * Reordering is up/down buttons, not drag-and-drop. A drag library would be a
 * dependency, a touch-target problem on the tablet this is used on, and
 * unusable with a keyboard — for a list capped at eight, two buttons are better
 * in every one of those ways.
 */
export function ProductGalleryPanel({ productId }: { productId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const key = ['product-images', productId];

  const { data: images, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => productImageService.list(productId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const addMutation = useMutation({
    mutationFn: (imageUrl: string) => productImageService.add(productId, imageUrl),
    onSuccess: () => {
      refresh();
      toast({ title: 'Photo added', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message || 'Could not add the photo', variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof productImageService.update>[2] }) =>
      productImageService.update(productId, id, patch),
    onSuccess: refresh,
    onError: (err: Error) => toast({ title: err.message || 'Could not save', variant: 'error' }),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => productImageService.reorder(productId, ids),
    onSuccess: refresh,
    onError: (err: Error) => toast({ title: err.message || 'Could not reorder', variant: 'error' }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => productImageService.remove(productId, id),
    onSuccess: () => {
      refresh();
      toast({ title: 'Photo removed', variant: 'success' });
    },
    onError: (err: Error) => toast({ title: err.message || 'Could not remove', variant: 'error' }),
  });

  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (error) return <Alert variant="danger" description="Could not load the gallery." />;

  const list = images ?? [];
  const full = list.length >= MAX_PRODUCT_IMAGES;

  /** Swap with a neighbour and send the WHOLE order — the API refuses a partial
   *  list rather than guessing where the rest belong. */
  const move = (index: number, delta: number) => {
    const next = [...list];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorderMutation.mutate(next.map((img) => img.id));
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Extra photos for your website. The main image above stays first.
        </p>
        <Badge variant={full ? 'warning' : 'neutral'}>
          {list.length} of {MAX_PRODUCT_IMAGES}
        </Badge>
      </div>

      {full ? (
        <Alert variant="info" description="Eight photos is the limit. Remove one to add another." />
      ) : (
        <FileUpload
          accept="image/*"
          maxSize={MAX_RAW_UPLOAD_BYTES}
          onUpload={async (files) => {
            if (!files.length) return;
            try {
              // Bytes go through the ordinary upload endpoint, which validates
              // magic bytes and recompresses; this records the key it returns.
              const uploadedKey = await uploadProductImage(files[0]);
              addMutation.mutate(uploadedKey);
            } catch {
              toast({ title: 'Photo upload failed', variant: 'error' });
            }
          }}
        />
      )}

      {list.length === 0 ? (
        <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
          No extra photos yet. Products with several photos sell noticeably better
          than products with one.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {list.map((image, index) => (
            <GalleryRow
              key={image.id}
              image={image}
              index={index}
              total={list.length}
              onMove={move}
              onAlt={(alt) => updateMutation.mutate({ id: image.id, patch: { alt_text: alt || null } })}
              onToggle={() =>
                updateMutation.mutate({ id: image.id, patch: { is_active: !image.is_active } })
              }
              onRemove={() => removeMutation.mutate(image.id)}
              busy={reorderMutation.isPending || removeMutation.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function GalleryRow({
  image,
  index,
  total,
  onMove,
  onAlt,
  onToggle,
  onRemove,
  busy,
}: {
  image: ProductImage;
  index: number;
  total: number;
  onMove: (index: number, delta: number) => void;
  onAlt: (alt: string) => void;
  onToggle: () => void;
  onRemove: () => void;
  busy: boolean;
}) {
  const [alt, setAlt] = useState(image.alt_text ?? '');

  return (
    <li className="flex items-start gap-4 rounded-xl border border-neutral-200 p-3 transition-all duration-200 hover:shadow-md dark:border-neutral-700">
      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-800">
        <img
          src={image.url}
          alt={image.alt_text ?? ''}
          className={`h-full w-full object-cover ${image.is_active ? '' : 'opacity-40 grayscale'}`}
        />
      </div>

      {/*
        Saved on BLUR, not on every keystroke: `Input`'s onChange fires per
        character, and wiring it straight to the mutation would be one PATCH
        per letter typed.
      */}
      <div
        className="min-w-0 flex-1"
        onBlur={() => {
          if (alt !== (image.alt_text ?? '')) onAlt(alt);
        }}
      >
        <Input
          label="Description for screen readers and search"
          value={alt}
          placeholder="What is in this photo?"
          onChange={setAlt}
          helperText="Optional. Left blank, the website numbers it — never the product name repeated, which is a duplicate-content signal."
        />
        {!image.is_active && (
          <p className="mt-1 text-xs text-warning-700 dark:text-warning-400">
            Hidden from the website. The file is kept, so this can be undone.
          </p>
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-1">
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="secondary"
            disabled={index === 0 || busy}
            onClick={() => onMove(index, -1)}
            aria-label="Move earlier"
          >
            ↑
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={index === total - 1 || busy}
            onClick={() => onMove(index, 1)}
            aria-label="Move later"
          >
            ↓
          </Button>
        </div>
        <Button size="sm" variant="secondary" onClick={onToggle} disabled={busy}>
          {image.is_active ? 'Hide' : 'Show'}
        </Button>
        {/* Delete loses the record for good; hiding is the reversible one, so
            it is the button next to it rather than buried. */}
        <Button size="sm" variant="danger" onClick={onRemove} disabled={busy}>
          Delete
        </Button>
      </div>
    </li>
  );
}
